// Installer pricing and quote calculation, shared by the console (Pricing tab +
// live preview) and, mirrored in Python, by the API that quotes real homeowners.
//
// The installer switches on the heat pump sizes they install and enters an
// installed price for each, BEFORE the grant. The widget sizes the heat pump for
// the home, picks the smallest switched-on size that is big enough, takes off
// the Boiler Upgrade Scheme grant THAT HOME qualifies for and shows a range
// either side of that price. The grant is worked out per home, never chosen by
// the installer: the Full calculator checks eligibility (grant.py in the API);
// the Compact one uses the home's heating (see compactGrant).

import type { PropertyDetails, Quote } from "./types";

/** Air source heat pump sizes (kW) installers can price: 1 kW steps to 12, then 14–20 in 2s and 24
 *  (large homes, often two units cascaded). Mirrored in the API (pricing.py, validation.py). */
export const KW_STEPS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24] as const;

/** Boiler Upgrade Scheme grant: the standard amount, and the uplift for homes
 *  replacing oil or LPG heating off the gas grid (until 31 March 2027). */
export const STANDARD_BUS_GRANT = 7500;
export const UPLIFT_BUS_GRANT = 9000;

/**
 * The grant the Compact calculator takes off, from the home's heating (the EPC's
 * main fuel, or the homeowner's answer): oil or LPG means off the gas grid, so
 * the uplift; anything else the standard grant. Mirrored in the API (pricing.py).
 */
export function compactGrant(heating: PropertyDetails["heating"] | undefined): number {
  return heating === "oil" || heating === "lpg" ? UPLIFT_BUS_GRANT : STANDARD_BUS_GRANT;
}

/** The quote shows the price ± this fraction, rounded to £50. */
export const QUOTE_SPREAD = 0.1;

/** Allowed installed price range per size, £ (mirrored by the API). */
export const PRICE_LIMITS = [1000, 100000] as const;

export interface PriceStep {
  kw: number;
  enabled: boolean; // switched off = never quoted (the price is kept for later)
  price: number | null; // £ installed, before BUS; required while enabled
}

export interface WidgetPricing {
  steps: PriceStep[];
}

/** Typical 2026 installed prices before BUS: placeholders in the editor, and the
 *  prices the preview uses until the installer has priced any size. */
export const SAMPLE_PRICES: Record<number, number> = {
  4: 9000, 5: 9500, 6: 10000, 7: 10500, 8: 11000, 9: 11500, 10: 12000, 11: 12500, 12: 13000, 14: 14000, 16: 15000, 18: 16000, 20: 17000, 24: 19000,
};

/**
 * One row per KW_STEPS size, keeping saved values. Sizes default to switched OFF
 * (installers switch on what they offer); older saved rows without an `enabled`
 * flag count as on only if priced.
 */
export function normalisePricing(input?: { steps?: Partial<PriceStep>[] } | null): WidgetPricing {
  const saved = new Map((input?.steps ?? []).map((s) => [s.kw, s]));
  return {
    steps: KW_STEPS.map((kw) => {
      const s = saved.get(kw);
      const price = s?.price ?? null;
      return { kw, price, enabled: s ? (s.enabled ?? price !== null) : false };
    }),
  };
}

/** Sizes that can be quoted: switched on and priced. */
export function usableSteps(p: WidgetPricing): { kw: number; price: number }[] {
  return p.steps.filter((s): s is PriceStep & { price: number } => s.enabled && s.price !== null);
}

/** True when the installer has at least one quotable size. */
export function hasPrices(p: WidgetPricing): boolean {
  return usableSteps(p).length > 0;
}

/** Price after the grant (never below zero). */
export function afterGrant(price: number, grant: number): number {
  return Math.max(0, price - grant);
}

/**
 * Rough heat pump size for a home, in kW. Bigger and less compact homes lose
 * more heat. The API will refine this with EPC floor area and fabric data.
 */
// Sizing constants (mirrored in the API's pricing.py): a base load plus an
// allowance per bedroom, scaled by how exposed the property type is
export const BASE_KW = 3;
export const KW_PER_BEDROOM = 1.6;
export const FORM_FACTORS: Record<PropertyDetails["propertyType"], number> = {
  detached: 1.25,
  "semi-detached": 1.0,
  terraced: 0.85,
  bungalow: 1.1,
  flat: 0.7,
};

export function estimateRequiredKw(d: PropertyDetails): number {
  return Math.round((BASE_KW + d.bedrooms * KW_PER_BEDROOM) * FORM_FACTORS[d.propertyType] * 10) / 10;
}

/**
 * Quote for a home: the smallest usable size that COVERS the required kW (a
 * smaller pump couldn't heat the home), minus the home's grant, ± QUOTE_SPREAD.
 * If no size offered is big enough there's no price: tooBig, and the installer
 * recommends a system after a survey. Uses SAMPLE_PRICES when the installer has
 * no usable sizes yet.
 */
export function quoteFor(pricing: WidgetPricing, requiredKw: number, grant: number = STANDARD_BUS_GRANT): Quote {
  const usable = hasPrices(pricing) ? usableSteps(pricing) : KW_STEPS.map((kw) => ({ kw, price: SAMPLE_PRICES[kw] }));
  const step = usable.find((s) => s.kw >= requiredKw);
  if (!step) {
    return { low: 0, high: 0, heatPumpKw: usable[usable.length - 1].kw, grant: 0, requiredKw, tooBig: true };
  }
  const net = afterGrant(step.price, grant);
  const round50 = (n: number) => Math.round(n / 50) * 50;
  return {
    low: round50(net * (1 - QUOTE_SPREAD)),
    high: round50(net * (1 + QUOTE_SPREAD)),
    heatPumpKw: step.kw,
    grant: Math.min(grant, step.price),
    price: step.price,
    requiredKw,
  };
}

/** One line of the calculation shown to homeowners. */
export interface WorkingStep {
  label: string;
  sum: string; // the arithmetic, in words and numbers
  result: string;
}

/**
 * The full calculation behind a quote, step by step, from the homeowner's
 * answers and the quote the API returned (so it always matches what was shown).
 * Null when the quote lacks the numbers (e.g. an older API).
 */
export function quoteWorking(d: PropertyDetails, q: Quote, method: "rdsap" | "legacy" = "rdsap"): WorkingStep[] | null {
  if (q.price === undefined || q.requiredKw === undefined || q.tooBig) return null;
  const gbp = (n: number) => `£${n.toLocaleString("en-GB")}`;
  const kw = (n: number) => `${Math.round(n * 10) / 10} kW`;
  const net = afterGrant(q.price, q.grant);
  const priced = [
    { label: "Heat pump size", sum: `smallest size installed that covers ${kw(q.requiredKw)}`, result: `${q.heatPumpKw} kW` },
    { label: "Installed price", sum: `${q.heatPumpKw} kW heat pump, before any grant`, result: gbp(q.price) },
    { label: "Boiler Upgrade Scheme", sum: `${gbp(q.price)} − ${gbp(q.grant)} grant`, result: gbp(net) },
    { label: "Your range", sum: `${gbp(net)} ± ${QUOTE_SPREAD * 100}%, rounded to £50`, result: `${gbp(q.low)} – ${gbp(q.high)}` },
  ];
  // Every estimate since the RdSAP calculator: the heat loss from the EPC (docs/03)
  if (method === "rdsap") {
    return [{ label: "Heat loss", sum: "RdSAP 10 method applied to the home's energy certificate", result: kw(q.requiredKw) }, ...priced];
  }
  const beds = d.bedrooms === 5 ? "5+" : String(d.bedrooms);
  // Older Compact estimates (before the RdSAP calculator): the bedrooms rule of thumb
  const base = BASE_KW + d.bedrooms * KW_PER_BEDROOM;
  const factor = FORM_FACTORS[d.propertyType];
  const type = d.propertyType.replace("-", " ");
  return [
    { label: "Base heat demand", sum: `${kw(BASE_KW)} + ${beds} bedrooms × ${kw(KW_PER_BEDROOM)}`, result: kw(base) },
    { label: "Property type", sum: `${type}: × ${factor.toFixed(2)}`, result: `${kw(q.requiredKw)} heat loss` },
    ...priced,
  ];
}
